<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div
        v-if="!showSpecificRecordDetails"
        class="header-extra"
      >
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('BUYERS_SEARCH_PH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!isFetchingBuyers"
      class="m-b-15 m-t-10"
    >
      <!-- Create Button -->
      <button
        v-if="hasCreateAccess && !showSpecificRecordDetails"
        type="button"
        :disabled="formActive && !activeBuyer"
        class="btn btn-success btn-sm"
        @click="showForm(null, $event)"
      >
        <i class="fa fa-plus" />
        <span v-t="'BUYERS_ADD_BUTTON'" />
      </button>

      <button
        v-if="showSpecificRecordDetails"
        class="btn btn-default btn-sm"
        @click="goToBuyers"
      >
        <i class="fa fa-arrow-left" />
        <span v-t="'BUYER_SHOW_ALL'" />
      </button>
    </div>

    <!-- Table Container -->
    <div
      v-if="!isFetchingBuyers"
      class="grid_table_holder"
    >
      <v-table
        :data="getDisplayBuyers"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'ACTIONS_COLUMN_HEADER'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <!-- Create New Form -->
          <tr
            v-if="showAddNewForm && hasCreateAccess"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <BuyerForm
                v-if="!showNewBuyerInvites"
                :show-loader="isCreateBuyerLoading"
                @cancel="hideForm"
                @submit="handleAddBuyerSubmit"
              />
              <BuyerInvites
                v-if="showNewBuyerInvites && invitesForBuyer"
                :buyer-id="invitesForBuyer.id"
                :close-btn-text="isNewUserToNewBuyerInvited ? 'Close' : 'Skip'"
                @close="onNewBuyerInvitesClose"
                @created-new-invite="onAddingNewInviteToNewBuyer"
              >
                <template
                  v-slot:title
                >
                  <p>
                    Invite Users to
                    <strong>{{ invitesForBuyer.name }}</strong>
                  </p>
                </template>
              </BuyerInvites>
            </td>
          </tr>

          <!-- Rows -->
          <template v-for="(buyer, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == buyer.id)"
              :key="buyer.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showCreateForm"
                  @click="displayMobileAction(buyer.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <td
                v-if="userVisibleColumns.includes('companyName')"
                :align="columnSettings['companyName'].rowAlign"
                data-testid="company-name"
              >
                <a
                  class="clickable"
                  :href="getBuyersEditHref(buyer.id)"
                  @click="showForm(buyer, $event)"
                >
                  {{ buyer.name }}
                </a>
              </td>

              <td
                v-if="userVisibleColumns.includes('subid')"
                :align="columnSettings['subid'].rowAlign"
              >
                {{ buyer.subId }}
              </td>

              <td
                v-if="userVisibleColumns.includes('buyerCanPauseTargets')"
                :align="columnSettings['buyerCanPauseTargets'].rowAlign"
              >
                <MiniToggle
                  :is-on="buyer.canPauseTargets"
                  :tooltip="
                    $t(
                      buyer.canPauseTargets
                        ? 'BUYER_CAN_PAUSE_TARGETS'
                        : 'BUYER_CAN_NOT_PAUSE_TARGETS'
                    )
                  "
                  @click="
                    toggleBuyerPermission({
                      buyer,
                      permissionProperty: 'canPauseTargets',
                    })
                  "
                />
              </td>

              <td
                v-if="
                  userVisibleColumns.includes('buyerCanSetTargetConcurrency')
                "
                :align="columnSettings['buyerCanSetTargetConcurrency'].rowAlign"
              >
                <MiniToggle
                  :is-on="buyer.canSetConcurrencyCaps"
                  :tooltip="
                    $t(
                      buyer.canSetConcurrencyCaps
                        ? 'BUYER_CAN_SET_CONCURRENCY_CAPS_TARGETS'
                        : 'BUYER_CAN_NOT_SET_CONCURRENCY_CAPS_TARGETS'
                    )
                  "
                  @click="
                    toggleBuyerPermission({
                      buyer,
                      permissionProperty: 'canSetConcurrencyCaps',
                    })
                  "
                />
              </td>

              <td
                v-if="
                  userVisibleColumns.includes('buyerCanSetTargetConcurrency')
                "
                :align="columnSettings['buyerCanSetTargetConcurrency'].rowAlign"
              >
                <MiniToggle
                  :is-on="buyer.canDisputeConversions"
                  :tooltip="
                    $t(
                      buyer.canDisputeConversions
                        ? 'BUYER_CAN_DISPUTE_CONVERSION'
                        : 'BUYER_CAN_NOT_DISPUTE_CONVERSION'
                    )
                  "
                  @click="
                    toggleBuyerPermission({
                      buyer,
                      permissionProperty: 'canDisputeConversions',
                    })
                  "
                />
              </td>

              <td
                v-if="userVisibleColumns.includes('restrictDuplicate')"
                :align="columnSettings['restrictDuplicate'].rowAlign"
              >
                <div
                  v-tooltip.right.hover="getPopoverText(buyer.routeDuplicates)"
                  aria-hidden="true"
                >
                  <span>{{ getRestrictionText(buyer.routeDuplicates) }}</span>
                  <span>{{
                    getRestrictionDurationText(buyer.routeDuplicates)
                  }}</span>
                  <span>{{
                    buyer.routeDuplicates &&
                      buyer.routeDuplicates.allowDuplicatesAfter !== 0 &&
                      buyer.routeDuplicates.previousCallState
                  }}</span>
                </div>
              </td>

              <td
                v-if="userVisibleColumns.includes('currentHourSum')"
                :align="columnSettings['currentHourSum'].rowAlign"
              >
                <LiveCounterCell
                  :column="'currentHourSum'"
                  :is-amount-type="true"
                  :usage-stats="buyer.usageStats"
                  :caps="buyer.caps"
                />
              </td>
              <td
                v-if="userVisibleColumns.includes('currentDaySum')"
                :align="columnSettings['currentDaySum'].rowAlign"
              >
                <LiveCounterCell
                  :column="'currentDaySum'"
                  :is-amount-type="true"
                  :usage-stats="buyer.usageStats"
                  :caps="buyer.caps"
                />
              </td>
              <td
                v-if="userVisibleColumns.includes('currentMonthSum')"
                :align="columnSettings['currentMonthSum'].rowAlign"
              >
                <LiveCounterCell
                  :column="'currentMonthSum'"
                  :is-amount-type="true"
                  :usage-stats="buyer.usageStats"
                  :caps="buyer.caps"
                />
              </td>
              <td
                v-if="userVisibleColumns.includes('totalSum')"
                :align="columnSettings['totalSum'].rowAlign"
              >
                <LiveCounterCell
                  :column="'totalSum'"
                  :is-amount-type="true"
                  :usage-stats="buyer.usageStats"
                  :caps="buyer.caps"
                />
              </td>

              <td
                v-if="userVisibleColumns.includes('status')"
                :align="columnSettings['status'].rowAlign"
              >
                <Status :entity="buyer" />
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <BuyerActions
                  :create-access="hasCreateAccess"
                  :current-buyer="buyer"
                  :disabled="formActive"
                  :is-admin="isAdmin"
                  :href="getBuyersEditHref(buyer.id)"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @show-edit-form="showForm"
                  @confirm-reset-cap-counters="openConfirmResetCapCountersModal"
                  @confirm-delete="openConfirmDeleteBuyerModal"
                  @show-invites="showInvites"
                  @show-references="showReferences"
                />
              </td>
            </tr>

            <!-- Edit buyer invites row - START -->
            <tr
              v-if="buyer.showInvites"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <td colspan="100">
                <BuyerInvites
                  :buyer-id="buyer.id"
                  @close="hideInvites(buyer)"
                />
              </td>
            </tr>
            <!-- Edit buyer invites row - END -->

            <!-- Edit form -->
            <tr
              v-if="buyer.showEditForm"
              :key="buyer.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <BuyerForm
                  :show-loader="isUpdateBuyerLoading"
                  :buyer="buyer"
                  @cancel="hideForm"
                  @submit="handleUpdaterBuyerSubmit"
                />
              </td>
            </tr>

            <!-- View buyer references row -->
            <tr
              v-if="buyer.showReferences"
              :key="buyer.id + '-references'"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <td colspan="100">
                <div class="p-15">
                  <legend class="f-s-13 p-b-10">
                    <span v-t="'BUYER_REFERENCES_TITLE'" />
                    <button 
                      @click="hideReferences(buyer)" 
                      type="button" 
                      class="btn btn-xs btn-danger legend-right-button"
                    >
                      <i class="typcn typcn-delete"></i>
                      Close
                    </button>
                  </legend>
                  
                  <BuyerReferences :buyer-id="buyer.id" />
                </div>
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == buyer.id"
              :key="buyer.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <BuyerActions
                  :create-access="hasCreateAccess"
                  :current-buyer="buyer"
                  :disabled="formActive"
                  :is-admin="isAdmin"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @show-edit-form="showForm"
                  @confirm-reset-cap-counters="openConfirmResetCapCountersModal"
                  @confirm-delete="openConfirmDeleteBuyerModal"
                  @show-invites="showInvites"
                  @show-references="showReferences"
                />
              </td>
            </tr>
          </template>

          <tr>
            <td
              v-if="
                !showAddNewForm &&
                  (!getDisplayBuyers || 0 === getDisplayBuyers.length)
              "
              v-t="'BUYERS_HEADER_NONE'"
              colspan="100%"
            />
          </tr>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="isFetchingBuyers"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'BUYER_HEADER_LOADING'" />
    </div>
    <!-- Delete Confirmation Modal Start -->
    <BuyerDeleteModal
      v-if="showDeleteModal"
      :buyer="buyerForDelete"
      :loading-deleting-buyer="isBuyerDeleteLoading"
      :loading-buyer-references="isBuyerReferencesLoading"
      :buyer-references="buyerReferences"
      :check-references-text="checkReferencesText"
      @confirm="confirmDelete"
      @close="closeDeleteModal"
    />
    <!-- Delete Confirmation Modal End -->

    <!-- Reset buyer counters modal start -->
    <BuyerResetCapCountersModal
      v-if="showResetCapCounterModal"
      :current-buyer="currentBuyer"
      :is-buyer-reset-cap-counters-loading="isBuyerResetCapCountersLoading"
      @confirm="resetCapCounters"
      @close="closeResetCapCounterModal"
    />

    <!-- Reset buyer counters modal end -->
  </PortalWidget>
</template>

<script>
import {
  buyerService,
  utilities,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';
import Loader from '@/core/components/Loader';
import PortalWidget from '@/core/components/PortalWidget.vue';
import { isMobile } from '@/core/utilities';
import Invites from '@/modules/invites/components/invites';
import { STATUS_ERROR, STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';
import sharedMessages from '@/core/locales';
import messages from '../locales';
import BuyerActions from './BuyerActions.vue';
import BuyerForm from './BuyerForm.vue';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import LiveCounterCell from '@/core/components/LiveCounterCell/LiveCounterCell.vue';
import Status from '@/core/components/Status/Status.vue';
import BuyerDeleteModal from './BuyerDeleteModal.vue';
import MiniToggle from '@/core/components/MiniToggle/MiniToggle.vue';
import BuyerInvites from './BuyerInvites.vue';
import BuyerReferences from './BuyerReferences.vue';
import BuyerResetCapCountersModal from './BuyerResetCapCountersModal.vue';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import {
  GET_BUYER,
  GET_BUYERS,
  RESET_BUYER_CALL_COUNTER,
  SET_WIDGET_TITLE,
  GET_BUYER_REFERENCES,
  TOGGLE_BUYER_PERMISSION,
  DELETE_BUYER,
  SET_INVITES_FOR_BUYER,
  SET_SHOW_NEW_BUYER_INVITES,
  UPDATE_BUYER,
  CREATE_BUYER,
} from '../store/actionTypes';
import { SET_BUYER } from '../store/mutationTypes';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'Buyers',
  components: {
    Loader,
    BuyerForm,
    BuyerActions,
    PortalWidget,
    ColumnToggle,
    ConfirmationModal,
    Invites,
    LiveCounterCell,
    Status,
    BuyerDeleteModal,
    MiniToggle,
    BuyerResetCapCountersModal,
    BuyerInvites,
    BuyerReferences,
  },
  data: () => ({
    isMobile: isMobile,
    showDeleteModal: false,
    showResetCapCounterModal: false,
    showSpecificRecordDetails: false,
    activeBuyer: null,
    formActive: false,
    showAddNewForm: false,
    activeActionBuyerId: false,
    isNewUserToNewBuyerInvited: false,
    currentBuyer: {},
    filters: {
      name: { value: '', keys: ['name', 'id', 'mapToTagType', 'mapToTagName'] },
    },
    currentPage: 1,
    totalPages: 0,
    activeActionRowId: null,
  }),
  beforeMount() {
    this[SET_WIDGET_TITLE]({ title: this.$i18n.t('BUYERS_HEADER') });
  },
  mounted() {
    this.showSpecificRecordDetails = utilities.getPropFromUrlParams('buyerId');
    this.loadData();
  },
  computed: {
    ...mapState(['currentAccountId']),
    ...mapState('buyersV2', [
      'buyerReferences',
      'checkReferencesText',
      'userVisibleColumns',
      'columnSettings',
      'widget',
      'invitesForBuyer',
      'showNewBuyerInvites',
    ]),
    ...mapGetters({
      isAdmin: 'isAdmin',
      hasAccessToItem: 'hasAccessToItem',
    }),
    ...mapGetters('buyersV2', [
      'getDisplayBuyers',
      'getBuyersStatus',
      'getBuyerStatus',
      'isFetchingBuyers',
      'resetBuyerCallCountersStatus',
      'getBuyerReferencesStatus',
      'isBuyerReferencesLoading',
      'isBuyerResetCapCountersLoading',
      'isBuyerDeleteLoading',
      'createBuyerStatus',
      'updateBuyerStatus',
      'deleteBuyerStatus',
      'toggleBuyerPermissionStatus',
      'handleLinkRpcStatus',
    ]),
    isCreateBuyerLoading() {
      return this.createBuyerStatus.status === STATUS_PENDING;
    },
    isUpdateBuyerLoading() {
      return this.updateBuyerStatus.status === STATUS_PENDING;
    },
    hasCreateAccess() {
      return this.hasAccessToItem('Buyer', 'CREATE');
    },
    disabled() {
      return (
        this.isBuyerDeleteLoading ||
        this.isBuyerResetCapCountersLoading ||
        this.showAddNewForm ||
        !!this.currentBuyer.id
      );
    },
  },
  created() {},
  methods: {
    ...mapActions('buyersV2', [
      SET_WIDGET_TITLE,
      GET_BUYERS,
      GET_BUYER,
      RESET_BUYER_CALL_COUNTER,
      GET_BUYER_REFERENCES,
      TOGGLE_BUYER_PERMISSION,
      SET_INVITES_FOR_BUYER,
      SET_SHOW_NEW_BUYER_INVITES,
      UPDATE_BUYER,
      CREATE_BUYER,
      SET_INVITES_FOR_BUYER,
      SET_SHOW_NEW_BUYER_INVITES,
      DELETE_BUYER,
    ]),
    ...mapMutations('buyersV2', [SET_BUYER]),
    getPopoverText(routeDuplicates) {
      if (
        !routeDuplicates ||
        routeDuplicates.allowDuplicatesAfter == null ||
        routeDuplicates.allowDuplicatesAfter === 0
      ) {
        return this.$t('BUYER_DUPLICATE_RESTRICTION_POPOVER_NONE');
      }

      const isAlways = routeDuplicates.allowDuplicatesAfter === -1;

      if (isAlways) {
        return this.$t('BUYER_DUPLICATE_RESTRICTION_POPOVER_ALWAYS', {
          previousState: routeDuplicates.previousCallState.toLowerCase(),
          restrictionText:
            this.getRestrictionText(routeDuplicates).toLowerCase(),
        });
      }

      return this.$t('BUYER_DUPLICATE_RESTRICTION_POPOVER_LIMITED', {
        previousState: routeDuplicates.previousCallState.toLowerCase(),
        restrictionText: this.getRestrictionText(routeDuplicates).toLowerCase(),
        days: routeDuplicates.allowDuplicatesAfter,
      });
    },
    getRestrictionDurationText(routeDuplicates) {
      if (
        !routeDuplicates ||
        routeDuplicates.allowDuplicatesAfter == null ||
        routeDuplicates.allowDuplicatesAfter === 0
      ) {
        return '';
      }

      if (routeDuplicates.allowDuplicatesAfter > 0) {
        return ' - ' + routeDuplicates.allowDuplicatesAfter + ' days - ';
      }

      return ' - ';
    },
    getRestrictionText(routeDuplicates) {
      let restrictionType = 'none';

      if (
        routeDuplicates &&
        routeDuplicates.trackBy &&
        routeDuplicates.allowDuplicatesAfter !== 0
      ) {
        restrictionType = routeDuplicates.trackBy;
      }

      return buyerService.getRestrictionText(restrictionType);
    },
    showForm(buyer, $event) {
      $event?.preventDefault();

      if (this.activeBuyer && this.activeBuyer.showInvites) {
        this.hideInvites(this.activeBuyer);
      }

      if (!this.showSpecificRecordDetails) {
        if (this.activeBuyer) {
          this.hideForm(this.activeBuyer);
        }
        this.activeBuyer = buyer;
        this.formActive = true;
      }
      if (buyer) {
        const _buyer = cloneDeep(buyer);
        this.showAddNewForm = false;
        _buyer.showEditForm = true;
        this.updateBuyerState(_buyer);
      } else {
        this.showAddNewForm = true;
      }
      this.activeActionBuyerId = false;
    },
    updateBuyerState(buyer) {
      if (!buyer) return;
      const buyerIndex = this.getDisplayBuyers.findIndex(
        (e) => e.id === buyer.id
      );
      this[SET_BUYER]({ index: buyerIndex, buyer });
    },
    hideForm(buyer) {
      const _buyer = cloneDeep(buyer);
      if (_buyer) {
        _buyer.showEditForm = false;
      } else {
        this.showAddNewForm = false;
      }
      this.formActive = false;
      this.activeBuyer = null;

      this.updateBuyerState(_buyer);

      if (this.showSpecificRecordDetails) {
        this.goToBuyers();
      }
    },
    goToBuyers() {
      $ngStateService.go('dashboard.buyers');
    },
    getBuyersEditHref(buyerId) {
      return $ngStateService.href(
        'dashboard.buyer.edit',
        { buyerId: buyerId },
        { absolute: true }
      );
    },
    async onNewBuyerInvitesClose() {
      await this[SET_SHOW_NEW_BUYER_INVITES]({ showNewBuyerInvites: false });
      await this[SET_INVITES_FOR_BUYER]({ buyer: undefined });
      this.isNewUserToNewBuyerInvited = false;
      this.hideForm();

      await this[GET_BUYERS]();
    },
    onAddingNewInviteToNewBuyer() {
      this.isNewUserToNewBuyerInvited = true;
    },
    async toggleBuyerPermission({ buyer, permissionProperty }) {
      await this[TOGGLE_BUYER_PERMISSION]({
        buyer,
        permissionProperty,
      });

      if (this.toggleBuyerPermissionStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.toggleBuyerPermissionStatus.error);
      }
    },
    async resetCapCounters() {
      await this[RESET_BUYER_CALL_COUNTER]({ buyerId: this.currentBuyer.id });

      if (this.resetBuyerCallCountersStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$t('RESET_COUNTERS_SUCCESS')
        );
        this.closeResetCapCounterModal();
      }

      if (this.resetBuyerCallCountersStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.resetBuyerCallCountersStatus.error);
      }
    },
    // confirmResetCapCounters
    openConfirmResetCapCountersModal(buyer) {
      if (!this.formActive) {
        this.currentBuyer = buyer;
        this.showResetCapCounterModal = true;
      }
    },
    showInvites(buyer) {
      if (!this.formActive) {
        const _buyer = cloneDeep(buyer);
        _buyer.showInvites = true;
        if (!this.showSpecificRecordDetails) this.formActive = true;
        this.activeBuyer = _buyer;
        this.activeActionBuyerId = false;
        this.updateBuyerState(_buyer);
      }
    },


    // Example: Show in an expandable row like invites
    async showReferences(buyer) {
      // Close other open sections
      if (this.activeBuyer && this.activeBuyer.showInvites) {
        this.hideInvites(this.activeBuyer);
      }
      
      // Update buyer state
      const _buyer = cloneDeep(buyer);
      _buyer.showReferences = true;
      this.updateBuyerState(_buyer);
      
      this.activeBuyer = _buyer;
      this.formActive = true;
    },

    hideReferences(buyer) {
      const _buyer = cloneDeep(buyer);
      _buyer.showReferences = false;
      this.updateBuyerState(_buyer);
      
      this.activeBuyer = null;
      this.formActive = false;
    },  

    activateBuyerActions(buyerId) {
      this.activeActionBuyerId =
        this.activeActionBuyerId == buyerId ? false : buyerId;
    },
    hideInvites(buyer) {
      const _buyer = cloneDeep(buyer);
      _buyer.showInvites = false;

      this.updateBuyerState(_buyer);

      this.activeBuyer = null;
      this.formActive = false;
    },
    onUpdate(buyer) {
      this.loadData();

      // Don't hide form when specific record details are only being shown
      if (!this.showSpecificRecordDetails) {
        this.hideForm(buyer);
      }
    },
    // confirmDeleteModal
    openConfirmDeleteBuyerModal(buyer) {
      this.buyerForDelete = buyer;
      this[GET_BUYER_REFERENCES]({ buyerId: buyer.id });
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      await this[DELETE_BUYER]({ buyerId: this.buyerForDelete.id });

      if (this.deleteBuyerStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$t('DELETE_BUYER_SUCCESSFUL_MESSAGE')
        );
        this.closeDeleteModal();
      }

      if (this.deleteBuyerStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.deleteBuyerStatus.error);
      }
    },
    // cancelModal
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.buyerForDelete = null;
    },
    // cancelModal
    closeResetCapCounterModal() {
      this.showResetCapCounterModal = false;
    },
    async handleAddBuyerSubmit(payload) {
      await this[CREATE_BUYER](payload);

      if (this.createBuyerStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('BUYER_NOTICE_CREATE'));
      }
      if (this.createBuyerStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.createBuyerStatus.error);
      } else if (this.handleLinkRpcStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.handleLinkRpcStatus.error);
      }
    },
    async handleUpdaterBuyerSubmit(payload) {
      await this[UPDATE_BUYER](payload);

      if (this.updateBuyerStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('BUYER_NOTICE_UPDATE'));
        this.onUpdate(payload.buyer);
      }

      if (this.updateBuyerStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.updateBuyerStatus.error);
      } else if (this.handleLinkRpcStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.handleLinkRpcStatus.error);
      }
    },
    loadData() {
      if (this.showSpecificRecordDetails) {
        this.loadBuyer(utilities.stateParams.buyerId);
      } else {
        this.loadBuyers();
      }
    },
    async loadBuyers() {
      await this[GET_BUYERS]();

      if (this.getBuyersStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getBuyersStatus.error);
      }
    },
    async loadBuyer(buyerId) {
      await this[GET_BUYER]({ buyerId: buyerId });

      if (this.getBuyerStatus.status === STATUS_SUCCESS) {
        this.showForm(this.getDisplayBuyers[0]);
      }

      if (this.getBuyerStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.getBuyerStatus.error);
      }
    },
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },

  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
